import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'takedown', label: 'Filing a Takedown Notice' },
  { id: 'where', label: 'Where to Send Notices' },
  { id: 'counter', label: 'Counter-Notification' },
  { id: 'repeat', label: 'Repeat Infringers' },
  { id: 'misrepresentation', label: 'Misrepresentation' },
];

const DmcaPolicy = () => (
  <LegalPageLayout title="DMCA Policy" lastUpdated="August 5, 2026" toc={TOC}>
    <LegalSection id="overview" number={1} title="Overview">
      <p>
        BookingFlows respects the intellectual property rights of others and expects the businesses
        and individuals using our platform (&ldquo;Operators&rdquo;) to do the same. In accordance with the
        Digital Millennium Copyright Act of 1998 (17 U.S.C. &sect; 512, the &ldquo;DMCA&rdquo;), we will respond
        expeditiously to valid claims of copyright infringement involving content hosted on or made
        available through the BookingFlows platform — including booking pages, service images,
        descriptions, and blog content uploaded by Operators.
      </p>
      <p>
        Content on Operator booking pages is uploaded and controlled by the Operators themselves.
        BookingFlows acts as a hosting service provider for that content within the meaning of the DMCA.
      </p>
    </LegalSection>

    <LegalSection id="takedown" number={2} title="Filing a Takedown Notice">
      <p>
        If you believe your copyrighted work has been copied and is accessible through BookingFlows in a
        way that constitutes infringement, please submit a written notification containing all of the following:
      </p>
      <ul>
        <li>A physical or electronic signature of the copyright owner, or a person authorized to act on their behalf;</li>
        <li>Identification of the copyrighted work claimed to have been infringed (or, if multiple works, a representative list);</li>
        <li>Identification of the material claimed to be infringing, with enough information for us to locate it (a direct URL is best);</li>
        <li>Your contact information — name, mailing address, telephone number, and email address;</li>
        <li>A statement that you have a good-faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law;</li>
        <li>A statement, made under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on the owner&apos;s behalf.</li>
      </ul>
      <p>
        Upon receipt of a valid notice, we will remove or disable access to the allegedly infringing
        material promptly and notify the Operator responsible for the content.
      </p>
    </LegalSection>

    <LegalSection id="where" number={3} title="Where to Send Notices">
      <LegalCallout>
        <p>
          Submit DMCA notices through our <a href="/support#tickets">Support Center</a> with the subject
          line &ldquo;DMCA Notice&rdquo;, or by mail to: BookingFlows, Attn: DMCA Agent — the mailing address is
          available on request through the Support Center.
        </p>
      </LegalCallout>
      <p>
        Notices that do not substantially comply with the requirements in Section 2 may not receive a response.
      </p>
    </LegalSection>

    <LegalSection id="counter" number={4} title="Counter-Notification">
      <p>
        If material you posted was removed as a result of a DMCA notice and you believe the removal was
        a mistake or misidentification, you may submit a written counter-notification containing:
      </p>
      <ul>
        <li>Your physical or electronic signature;</li>
        <li>Identification of the material that was removed and its location before removal;</li>
        <li>A statement, under penalty of perjury, that you have a good-faith belief the material was removed as a result of mistake or misidentification;</li>
        <li>Your name, address, and telephone number, and a statement that you consent to the jurisdiction of the federal district court for your judicial district (or, if outside the United States, any judicial district in which BookingFlows may be found), and that you will accept service of process from the person who filed the original notice.</li>
      </ul>
      <p>
        Upon receipt of a valid counter-notification, we will forward it to the original complainant. If the
        complainant does not notify us within 10&ndash;14 business days that they have filed a court action, we
        may restore the removed material.
      </p>
    </LegalSection>

    <LegalSection id="repeat" number={5} title="Repeat Infringers">
      <p>
        BookingFlows will, in appropriate circumstances, suspend or terminate the accounts of Operators
        who are determined to be repeat infringers.
      </p>
    </LegalSection>

    <LegalSection id="misrepresentation" number={6} title="Misrepresentation">
      <p>
        Under 17 U.S.C. &sect; 512(f), any person who knowingly materially misrepresents that material is
        infringing, or that it was removed by mistake, may be liable for damages — including costs and
        attorneys&apos; fees. Please do not submit false claims.
      </p>
    </LegalSection>
  </LegalPageLayout>
);

export default DmcaPolicy;
